import { Grid } from "@mui/material";
import { zodResolver } from "@hookform/resolvers/zod";
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDTypography from "components/MDTypography";
import Modal from "modules/Modal/Modal";
import { CreatePlantInput, Plant, UpdatePlantInput } from "generated/graphql";
import useCreatePlant from "hooks/organization/plants/useCreatePlant";
import useUpdatePlant from "hooks/organization/plants/useUpdatePlant";
import AddProductsSection from "modules/OrganizationPlants/CreatePlant/AddProductsSection/AddProductsSection";
import MainForm from "modules/OrganizationPlants/CreatePlant/MainForm/MainForm";
import { defaultValues as createPlantDefaultValues, schema } from "DDD/action-objects/PlantCreate";
import { useForm, FormProvider } from "react-hook-form";
import { useEffect, useRef } from "react";
import PageTour, { PageTourHandle } from "components/PageTour/PageTour";
import trialUserEditPlantInformationTourSteps from "./TrialUserEditPlantInformation.tour";

type TrialUserEditPlantInformationProps = {
  open: boolean;
  onClose: () => void;
  plant?: Plant | null;
  stepOffset?: number;
  totalSteps?: number;
};

function EditPlantForm({ plant, onSuccess }: { plant: Plant; onSuccess: () => void }) {
  const [updatePlant, { isSuccess }] = useUpdatePlant();
  const methods = useForm<UpdatePlantInput>({
    defaultValues: {
      ...plant,
      products: {
        upsert: plant.products.map((p) => ({
          id: p.id,
          rate: p.rate,
          type: p.type,
        })),
      },
    },
  });

  const { handleSubmit } = methods;

  return (
    <FormProvider {...methods}>
      <MDBox
        p={3}
        component="form"
        role="form"
        onSubmit={handleSubmit(async (values: UpdatePlantInput) => {
          const data = JSON.parse(JSON.stringify(values));
          delete data.__typename;
          const products = values.products.upsert.map((p) => ({
            id: p.id,
            rate: p.rate,
            type: p.type,
          }));
          const result = await updatePlant({
            variables: {
              input: {
                ...data,
                products: { upsert: products },
              },
            },
          });
          const success = isSuccess(result.data);
          if (success) {
            onSuccess();
          }
        })}
      >
        <MainForm />
        <MDBox mt={1}>
          <Grid container spacing={3}>
            <AddProductsSection fieldArrayName="products.upsert" />
            <Grid item xs={12} display="flex" justifyContent="flex-end">
              <MDButton type="submit" variant="gradient" color="success">
                Save
              </MDButton>
            </Grid>
          </Grid>
        </MDBox>
      </MDBox>
    </FormProvider>
  );
}

function CreatePlantForm({ onSuccess }: { onSuccess: () => void }) {
  const [createPlant, { isSuccess }] = useCreatePlant();
  const methods = useForm<CreatePlantInput>({
    resolver: zodResolver(schema),
    defaultValues: createPlantDefaultValues,
  });

  const { handleSubmit } = methods;

  return (
    <FormProvider {...methods}>
      <MDBox
        p={3}
        component="form"
        role="form"
        onSubmit={handleSubmit(async (values: CreatePlantInput) => {
          const products = values.products.create.map((p) => ({
            rate: p.rate,
            type: p.type,
          }));
          const result = await createPlant({
            variables: {
              input: {
                ...values,
                products: { create: products },
              },
            },
          });
          const success = isSuccess(result.data);
          if (success) {
            onSuccess();
          }
        })}
      >
        <MainForm />
        <MDBox mt={1}>
          <Grid container spacing={3}>
            <AddProductsSection fieldArrayName="products.create" />
            <Grid item xs={12} display="flex" justifyContent="flex-end">
              <MDButton type="submit" variant="gradient" color="success">
                Save
              </MDButton>
            </Grid>
          </Grid>
        </MDBox>
      </MDBox>
    </FormProvider>
  );
}

export default function TrialUserEditPlantInformation({
  open,
  onClose,
  plant,
  stepOffset,
  totalSteps,
}: TrialUserEditPlantInformationProps) {
  const tourRef = useRef<PageTourHandle>(null);

  useEffect(() => {
    if (!open) return;
    // Defer until after the modal's content has mounted and painted, otherwise
    // Joyride can try to position against targets that aren't in the DOM yet.
    const timeout = setTimeout(() => tourRef.current?.replay(), 50);
    return () => clearTimeout(timeout);
  }, [open]);

  return (
    <Modal open={open} onClose={onClose} styles={{ maxHeight: "90vh", overflowY: "auto" }}>
      <PageTour
        ref={tourRef}
        tourKey="trialUserEditPlantInformation"
        steps={trialUserEditPlantInformationTourSteps}
        stepOffset={stepOffset}
        totalSteps={totalSteps}
      />
      <MDTypography variant="h6" p={3} pb={0}>
        {plant ? "Edit Plant Information" : "Add Plant Information"}
      </MDTypography>
      {plant ? (
        <EditPlantForm plant={plant} onSuccess={onClose} />
      ) : (
        <CreatePlantForm onSuccess={onClose} />
      )}
    </Modal>
  );
}
